import React, { useEffect, useRef, useState } from "react";
import "../css/TripCard.css";

const TripCard = ({ title, startDate, endDate, description, photo, photoSource, footerName, isClickable, hasOpenRegistration, eagerLoadImage = false }) => {
  const maxDescriptionLength = 300;
  const imageRef = useRef(null);

  const getThumbnailUrl = (url) => {
    if (!url) return url;

    const queryIndex = url.indexOf("?");
    const urlWithoutQuery = queryIndex >= 0 ? url.slice(0, queryIndex) : url;
    const query = queryIndex >= 0 ? url.slice(queryIndex) : "";
    const extensionIndex = urlWithoutQuery.lastIndexOf(".");

    if (extensionIndex < 0) return `${urlWithoutQuery}_thumbnail${query}`;

    return `${urlWithoutQuery.slice(0, extensionIndex)}_thumbnail${urlWithoutQuery.slice(extensionIndex)}${query}`;
  };

  const thumbnailPhoto = getThumbnailUrl(photo);
  const [shouldLoadFullImage, setShouldLoadFullImage] = useState(eagerLoadImage);
  const [displayedPhoto, setDisplayedPhoto] = useState(thumbnailPhoto);

  useEffect(() => {
    setDisplayedPhoto(thumbnailPhoto);
    setShouldLoadFullImage(eagerLoadImage);
  }, [photo, thumbnailPhoto, eagerLoadImage]);

  useEffect(() => {
    if (eagerLoadImage) return undefined;

    const imageElement = imageRef.current;
    if (!imageElement || typeof IntersectionObserver === "undefined") {
      setShouldLoadFullImage(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadFullImage(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "400px 0px",
        threshold: 0.01,
      }
    );

    observer.observe(imageElement);

    return () => observer.disconnect();
  }, [eagerLoadImage]);

  useEffect(() => {
    if (!shouldLoadFullImage || !photo || displayedPhoto === photo) return undefined;

    let cancelled = false;
    const fullImage = new Image();
    fullImage.src = photo;
    fullImage.onload = async () => {
      try {
        if (typeof fullImage.decode === "function") {
          await fullImage.decode();
        }
      } catch {
        // The image has already loaded, so fall back to swapping it even if decode() fails.
      }

      if (!cancelled) {
        setDisplayedPhoto(photo);
      }
    };

    return () => {
      cancelled = true;
      fullImage.onload = null;
    };
  }, [shouldLoadFullImage, photo, displayedPhoto]);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const areDatesEqual = (d1, d2) => {
    const date1 = new Date(d1);
    const date2 = new Date(d2);
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const displayDate =
    startDate && endDate
      ? areDatesEqual(startDate, endDate)
        ? formatDate(startDate)
        : `${formatDate(startDate)} - ${formatDate(endDate)}`
      : "";

  const trimmedDescription =
    description && description.length > maxDescriptionLength
      ? description.slice(0, maxDescriptionLength) + "..."
      : description;

  return (
    <div className="trip-card">
      <div className="trip-card-header">
        <h2 className="trip-card-title">{title}</h2>
        <p className="trip-card-dates">{displayDate}</p>
      </div>

      <div className="trip-card-image">
        <img
          ref={imageRef}
          src={displayedPhoto}
          alt={title}
          loading={eagerLoadImage ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={eagerLoadImage ? "high" : "auto"}
        />
        {hasOpenRegistration && (
          <div className="trip-card-corner-ribbon"><span>Zapisy</span></div>
        )}
        {photoSource && <p className="trip-card-photo-source">{photoSource}</p>}
      </div>

      <div className="trip-card-description">
        <p>{trimmedDescription}</p>
      </div>

      {footerName && (
        <div className="trip-card-footer">
          {isClickable ? (
            <span className="trip-card-link">Zobacz szczegóły <span aria-hidden="true">→</span></span>
          ) : (
            <span className="trip-card-link disabled" aria-disabled="true">{footerName}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default TripCard;
